/**
 * VCM (Variant Collection Manager) — shared browser-side utilities.
 *
 * Owns the window.VCM namespace and the Liquid/JS contract around it:
 * - VCM.currentColorValue: lowercased colour of the currently selected
 *   variant, broadcast by VariantSelects (global.js) on every change
 * - 'vcm:colorChange' event: dispatched on window whenever the colour
 *   changes; detail = { sectionId, color }
 * - findPrimaryVariant: JS twin of snippets/vcm-primary-variant.liquid
 *   (extended with group_label matching for recommendation entries)
 *
 * Loaded from layout/theme.liquid BEFORE global.js (defer order).
 *
 * Debug: append ?vcm_debug to any URL — the same flag the Liquid side
 * uses — or set window.VCM_DEBUG = true in the console at runtime.
 */
(function () {
  'use strict';

  var VCM = (window.VCM = window.VCM || {});

  VCM.COLOR_CHANGE_EVENT = 'vcm:colorChange';

  VCM.debug = /[?&]vcm_debug\b/.test(window.location.search);

  VCM.log = function () {
    if (VCM.debug || window.VCM_DEBUG) console.log.apply(console, arguments);
  };

  VCM.warn = function () {
    if (VCM.debug || window.VCM_DEBUG) console.warn.apply(console, arguments);
  };

  /**
   * Record the currently selected colour and broadcast it so listening
   * components (vcm-compact-recommendations, related-products link
   * rewriting) can react.
   */
  VCM.setCurrentColor = function (color, sectionId) {
    VCM.currentColorValue = color;
    try {
      window.dispatchEvent(
        new CustomEvent(VCM.COLOR_CHANGE_EVENT, {
          detail: { sectionId: sectionId, color: color },
        })
      );
    } catch (e) {
      VCM.warn('[VCM] Failed to broadcast color change', e);
    }
  };

  VCM.getCurrentColor = function () {
    return (VCM.currentColorValue || '').trim();
  };

  /**
   * Find the primary variant to display for a product (AJAX product JSON
   * shape, /products/{handle}.json).
   *
   * For variant-type recommendation entries, match rec.group_label against
   * option1/2/3 to find the right colour group, then return the first
   * available variant within it — falling back to a specific stored
   * variant_id risks picking an out-of-stock size.
   *
   * Note: the AJAX product JSON may omit `available` at the variant level;
   * undefined/null is treated as available. It carries no metafields, so
   * unlike snippets/vcm-primary-variant.liquid the draft state cannot be
   * honoured here — the Liquid-rendered fallbacks handle draft variants.
   */
  VCM.findPrimaryVariant = function (product, rec) {
    if (!product || !product.variants || product.variants.length === 0) return null;

    if (rec && rec.type === 'variant' && rec.group_label) {
      var label = String(rec.group_label).toLowerCase().trim();
      var colorVariants = product.variants.filter(function (v) {
        return (
          (v.option1 && String(v.option1).toLowerCase().trim() === label) ||
          (v.option2 && String(v.option2).toLowerCase().trim() === label) ||
          (v.option3 && String(v.option3).toLowerCase().trim() === label)
        );
      });
      if (colorVariants.length > 0) {
        var firstAvail = colorVariants.find(function (v) {
          return v.available !== false;
        });
        return firstAvail || colorVariants[0];
      }
      // Fallback: try the stored specific variant ID
      if (rec.variant_id) {
        var numId = String(rec.variant_id).split('/').pop();
        var byId = product.variants.find(function (v) {
          return String(v.id) === numId;
        });
        if (byId) return byId;
      }
    }

    var firstAvailable = product.variants.find(function (v) {
      return v.available !== false;
    });
    return firstAvailable || product.variants[0];
  };
})();
